/**
 * Safe chained function
 *
 * Will only create a new function if needed,
 * otherwise will pass back existing functions or null.
 *
 * @param {function} funcs - functions to chain
 * @returns {function|null}
 */
export const createChainedFunction = (...funcs) => funcs.reduce(
  (acc, func) => {
    if (func == null) {
      return acc;
    }

    return function chainedFunction(...args) {
      acc.apply(this, args);
      func.apply(this, args);
    };
  },
  () => {}
);

/**
 * Convert a shopify image url to Imgix
 * @note: Imgix domain is configured in .env (through CI)
 * @param {String} shopifyUrl - image url from shopify cdn
 * @param {Array} params - Imgix params, or use the default one for product image
 * @returns {String} - the ready to render image url
 */
export const getImgixUrl = (shopifyUrl, params = [
  'fit=crop',
  'h=625',
  'w=500',
]) => {
  if (!shopifyUrl) return shopifyUrl;
  if (!process.env.REACT_APP_IMGIX_URL) return shopifyUrl;

  const [path, filename] = shopifyUrl.split('/').slice(-2);

  return [
    `https://${process.env.REACT_APP_IMGIX_URL}/${path}/${filename}`,
    ...params,
    'auto=compress,format'
  ].join('&');
};

/**
 * Convert an img to blob url
 * @param {String} url - image url
 * @returns {Promise<String>} - a promise that contain the encoded Blob url
 */
export const toBlobUrl = url => fetch(url, { cache: 'force-cache' })
  .then(response => response.blob())
  .then(blob => URL.createObjectURL(blob));

/**
 * Decode base64 encoded variantId to original numeric Id
 * @param {String} encoded - variantId in base64
 * @returns {String} - numeric variant id in string
 */
export const decodeVariantId = encoded => atob(encoded).split('ProductVariant/')[1];

/**
 * API wrapper for Shopify's ajax change items in cart, only one item at a time
 * @param {String} id - product variant id
 * @param {Number} quantity - number of items to be added to cart
 * @param {Function} successCallback - whether or not load the shopping cart after the add
 * @returns {Promise} - ajax cart promise
 */
export const adjustCart = (id, quantity, successCallback) => (
  window.ShopifyAPI.adjustItem(id, quantity, successCallback)
);

/**
 * API wrapper for Shopify's ajax add to cart
 * @param {String} id - product variant id
 * @param {Number} quantity - number of items to be added to cart
 * @param {Function} successCallback - function to fire when add to cart success
 * @param {Function} errorCallback - function to fire when add to cart fail
 * @returns {Promise} - ajax cart promise
 */
export const addToCart = (id, quantity = 1, successCallback, errorCallback) => {
  const formData = new FormData();
  formData.append('id', id);
  formData.append('quantity', String(quantity));
  return window.ShopifyAPI.addItemFromForm(formData, successCallback, errorCallback);
};

/**
 * API wrapper for Shopify's ajax add to cart
 * @param {Array} items - products to be add
 * @returns {Promise} - ajax cart promise
 */
export const addMultipleToCart = async (items) => {
  if (!Array.isArray(items) || !items.length) return;

  try {
    await window.ShopifyAPI.addMultipleToCart(items, window.ajaxCart.load);
  } catch (e) {
    // TODO: Better error handling, jquery.ajax is not catching the error in $.ajax().error()
    // eslint-disable-next-line no-console
    console.error(e.responseText);
  }
};

/**
 * Add multiple variants to shopping cart, by queuing the ajax calls
 * @deprecated
 * @param {Array} items - list of variants to be added to cart, each item has {id, quantity}
 * @returns {Promise<void>} - add to cart promise
 */
export const addMultipleToCartOld = async (items) => {
  if (Array.isArray(items)) {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.id) {
        const isLastItem = i === items.length - 1;
        const variantId = decodeVariantId(item.id);
        // eslint-disable-next-line no-await-in-loop
        await addToCart(variantId, item.quantity || 1, isLastItem ? window.ajaxCart.load : null);
      }
    }
  }
};

/**
 * Get a permalink to add line items to cart page so user can share the link
 * @param {Array} items - list of variants to be added to cart, each item has {id, quantity}
 * @param {Number} quantity - number of items to be added to cart
 * @returns {string}
 */
export const getCartPermalink = (items, quantity = 1) => {
  let url = '';
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.id) {
      url += `${encodeURIComponent('id[]')}=${decodeVariantId(item.id)}&`;
    }
  }
  return url && `${window.location.origin}/cart/add?${url}quantity=${quantity}`;
};

/**
 * Get the Shopify handle from current URL
 * @returns {String} - the last part of the url, which is a Shopify handle
 */
export const getUrlHandle = () => window.location.href.split('/').slice(-1)[0].split('?')[0];

/**
 * Get variant from product data by variantId
 * @param {Object} productData - the product object from Storefront API
 * @param {String} variantId - base64 encoded variantId
 * @returns {Object|null|undefined} - variant node
 */
export const getVariantFromProduct = (productData, variantId) => {
  const product = productData.node || productData;
  if (product && product.variants) {
    return product.variants.edges.filter(variant => variant.node.id === variantId)[0];
  }
  return null;
};

/**
 * Push the data to the Google Tag Manager. Add ck namespace.
 * More info in here: https://developers.google.com/tag-manager/quickstart
 * @param {String} event - the GTM event, e.g. "productClick"
 * @param {Object} data -  The data to be pushed to google data layer.
 */
export const recordGTM = (event, data) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: `ck.${event}`,
    ck: {
      ...data
    }
  });
};

/**
 * @todo remove, only used in legacy steadlane stuff
 * @returns {string}
 */
export const storeLocation = () => {
  let store = null;
  switch (window.location.hostname) {
    case 'www.culturekings.com.au':
      store = 'culture-kings-au';
      break;
    case 'www.culturekings.co.nz':
      store = 'culture-kings-nz';
      break;
    default:
      store = 'culture-kings-dev';
  }
  return store;
};

/**
 * get the location code from env variables
 * @returns {string} location code
 */
export const getStoreLocation = () => {
  switch (process.env.REACT_APP_STORE) {
    case 'culture-kings-2.myshopify.com':
      return 'AU';
    case 'culture-kings-nz.myshopify.com':
      return 'NZ';
    case 'culture-kings-us.myshopify.com':
      return 'US';
    case 'culture-kings-uat.myshopify.com':
      return 'UAT';
    default:
      return 'DEV';
  }
};

/**
 * get help center url
 * @returns {string} url
 */
export const getHelpCenterUrl = () => {
  switch (process.env.REACT_APP_STORE) {
    case 'culture-kings-nz.myshopify.com':
      return 'https://help.culturekings.co.nz';
    case 'culture-kings-us.myshopify.com':
      return 'https://help.culturekings.com';
    default:
      return 'https://help.culturekings.com.au';
  }
};

/**
 * Handleize the string from "a b'c,d" to "a-b-c-d"
 * @param str
 * @returns {string}
 */
export const handleize = str => (
  str
    .toLowerCase()
    .replace(/[^\w\u00C0-\u024f]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace('-brand-', '-')
);

/**
 * Get brand image url from S3/Imgix
 * @todo: lots of hacks for custom brands, form a naming convention when upload images
 * @param vendor
 * @param align
 * @returns {string}
 */
export const getBrandImageUrl = (vendor, align = 'centre') => {
  const handle = handleize(vendor)
    .replace('champion-blanks', 'champion')
    .replace('notforsale', 'culture-kings')
    .replace('culture-kings-custom', 'culture-kings');

  return `https://culture-kings-static.imgix.net/images/logos-${align}/brand-${handle}.jpg?h=100&auto=compress,format`;
};

/**
 * Normalise brand name some have special chars
 * @param text
 * @returns {string}
 */
export const normaliseBrand = (text) => {
  const brand = text.toLowerCase();
  switch (brand) {
    case 'carre':
      return 'carré';
    case 'lil homme':
      return 'lil hommé';
    case 'mitchell and ness':
      return 'mitchell & ness';
    case 'crooks and castles':
      return 'crooks & castles';
    case 'the anti order':
      return 'The Anti-Order';
    default:
      return brand;
  }
};

/**
 * add/remove body-overlay class to disable/enable scroll behind the modal
 * @param show
 * @param id - identifier of the overlay owner
 */
export const toggleBodyOverlay = (show, id) => {
  const className = `body-overlay-${id}`;
  document.body.classList.toggle(className, show);
  document.documentElement.classList.toggle(className, show);
};

/**
 * Find and return duplicated values in given array
 * @param {Array} items - Simple primitives value arrays
 * @returns {Array} A new array that only contains duplicated items, one for each duplicates
 */
export const findDupesInArray = (items) => items.reduce(
  (acc, v, i, arr) => (arr.indexOf(v) !== i && acc.indexOf(v) === -1 ? acc.concat(v) : acc),
  []
);

/**
 * Return items in the array that is duplicated N times
 * @param {Array} items - Simple primitives value arrays
 * @param {Number} dupesCount - number of times the item is duplicated
 * @returns {Array} All items that are duplicated N times
 */
export const findDupesInArrayByCount = (items, dupesCount) => {
  const counts = items.reduce((map, val) => ({
    ...map,
    [val]: (map[val] || 0) + 1
  }), {});

  return Object.keys(counts).filter(key => counts[key] === dupesCount);
};

/**
 * Convert tag string to hash
 * @param {string} tag - bundle tag, example: "script=bundle:id=1588842633:value=14995:count=3:index=2:indexUnit=3";
 * @returns {object} Script hash: { id: xxxx, value: xxxx, count: xx,  index: x, indexUnit: x }
 */
export const scriptTagToHash = tag => tag.split(':').reduce((hash, subTag) => {
  const [key, val] = subTag.split('=');
  return {
    ...hash,
    [key]: val
  };
}, {});

/**
 * set an element backgroundImage when it's in the window view
 * @param target DOM Object
 * @param url backgroundImage url
 */
export const lazyLoadBackgroundImage = (target, url) => {
  if (!target) return;
  const observer = new IntersectionObserver(entries => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.style.backgroundImage = url;
        observer.unobserve(target);
      }
    });
  });
  observer.observe(target);
};

/**
 *
 * @param {String | Number} count - the word count
 * @param {String} singular
 * @param {String} plural
 * @returns {String}
 */
export const pluralize = (count, singular, plural) => (Number(count) > 1 ? plural : singular);

/**
 * Convert the search state object to facet combinations
 * @param searchState
 * @returns {string}
 */
const convertSearchStateToPriceRange = (searchState) => {
  const { max, min } = searchState.price; // Destructure max and min
  const parts = []; // Initialize an array to hold parts of the result

  if (min !== undefined) parts.push(`min: ${min}`); // Add min if defined
  if (max !== undefined) parts.push(`max: ${max}`); // Add max if defined

  return parts.join(','); // Join parts with a comma and return
};

/**
 * Convert the search state object to facet combinations
 * @param searchState
 * @returns {string}
 */
const convertSearchStateToFacetCombinations = (searchState) => Object.entries(searchState)
  .map(([key, value]) => {
    if (Array.isArray(value)) {
      return `${key}:${value.join(',')}`;
    }

    return `${key}:${value}`;
  })
  .join('|');

/**
 * convert menu categories into facet combinations
 * @param menuCategories
 * @returns {`menu.categories:${*}|menu.subCategories:${*}`|`menu.categories:${*}`}
 */
const convertMenuCategoriesToFacetCombinations = (menuCategories) => {
  const [category, subCategory] = menuCategories?.replace(/\s/g, '').split('>');

  if (subCategory) {
    return `menu.categories:${category}|menu.subCategories:${subCategory}`;
  }
  return `menu.categories:${category}`;
};

/**
 * Remove a facet from a string
 * @param facetString
 * @param facetName
 * @returns {string}
 */
const removeFacet = (facetString, facetName) => {
  // Create a dynamic regular expression to match the facet_name up to the next '|' or end of the string
  const regex = new RegExp(`(?:\\|)?${facetName}:[^|]*?(?=\\||$)`, 'g');

  // Remove the facet and any preceding or trailing '|' if it exists
  return facetString.replace(regex, '').replace(/(^[,|]+|[,|]+$)/g, '');
};

/**
 * Generate facet combinations from the search state object
 * @param searchState
 * @returns {string}
 */
export const generateFacetCombinations = (searchState, facetName) => {
  let combinations = '';

  if (searchState?.hierarchicalMenu?.['menu.categories']) {
    combinations += convertMenuCategoriesToFacetCombinations(searchState?.hierarchicalMenu?.['menu.categories']);
  }

  if (searchState?.refinementList && Object.keys(searchState.refinementList).length > 0) {
    combinations += combinations ? '|' : '';
    combinations += convertSearchStateToFacetCombinations(searchState.refinementList);
  }

  if (searchState?.toggle) {
    combinations += combinations ? '|' : '';
    combinations += convertSearchStateToFacetCombinations(searchState.toggle);
  }

  if (searchState?.range) {
    combinations += combinations ? '|' : '';
    combinations += convertSearchStateToPriceRange(searchState.range);
  }

  return removeFacet(combinations, facetName);
};
export const getQueryParameter = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams?.get('query') || urlParams?.get('Q');
};
